import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Eye, EyeOff, Upload, X } from "lucide-react";
import { api, assetUrl, formatApiErrorDetail } from "../../lib/api";

export default function CrudManager({ collection, title, singular, fields, listColumns }) {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const load = () => api.get(`/admin/${collection}`).then(({ data }) => setItems(data));
  useEffect(() => { load(); }, [collection]);

  const openNew = () => {
    const initial = {};
    fields.forEach((f) => { initial[f.key] = f.type === "list" || f.type === "tags" ? [] : f.type === "number" ? 0 : f.key === "status" ? "draft" : ""; });
    setForm(initial);
    setEditing("new");
  };

  const openEdit = (item) => {
    setForm({ ...item });
    setEditing(item.id);
  };

  const save = async () => {
    setSaving(true);
    try {
      if (editing === "new") {
        await api.post(`/admin/${collection}`, form);
        toast.success(`${singular} created`);
      } else {
        await api.put(`/admin/${collection}/${editing}`, form);
        toast.success(`${singular} updated`);
      }
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (item) => {
    if (!window.confirm(`Delete "${item.name || item.title}"? This cannot be undone.`)) return;
    await api.delete(`/admin/${collection}/${item.id}`);
    toast.success(`${singular} deleted`);
    load();
  };

  const togglePublish = async (item) => {
    const status = item.status === "published" ? "draft" : "published";
    await api.put(`/admin/${collection}/${item.id}`, { status });
    toast.success(status === "published" ? "Published" : "Unpublished");
    load();
  };

  return (
    <div data-testid={`admin-${collection}-page`}>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">{title}</h1>
          <p className="text-sm text-slate-500 mt-1">{items.length} items</p>
        </div>
        <button data-testid={`add-${collection}-button`} onClick={openNew}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-sky-500 text-white text-sm font-semibold hover:bg-sky-600 active:scale-95 transition-colors duration-200">
          <Plus className="w-4 h-4" aria-hidden="true" /> New {singular}
        </button>
      </div>

      <div className="mt-6 bg-white rounded-lg border border-border overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 border-b border-border">
              {listColumns.map((c) => <th key={c.key} className="text-left px-4 py-3 font-heading font-semibold text-slate-600 text-xs uppercase tracking-wide">{c.label}</th>)}
              <th className="px-4 py-3 w-32"></th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr key={item.id} className="border-b border-border last:border-0 hover:bg-slate-50" data-testid={`row-${item.slug || item.id}`}>
                {listColumns.map((c) => (
                  <td key={c.key} className="px-4 py-3 text-slate-700">
                    {c.key === "status" ? (
                      <span className={`text-xs px-2 py-1 rounded-full font-semibold ${item.status === "published" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                        {item.status || "—"}
                      </span>
                    ) : (
                      <span className="line-clamp-1">{Array.isArray(item[c.key]) ? item[c.key].join(", ") : String(item[c.key] ?? "")}</span>
                    )}
                  </td>
                ))}
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-1">
                    {"status" in item && (
                      <button data-testid={`toggle-publish-${item.slug || item.id}`} onClick={() => togglePublish(item)} title={item.status === "published" ? "Unpublish" : "Publish"}
                        className="p-1.5 rounded hover:bg-slate-200 text-slate-500">
                        {item.status === "published" ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                      </button>
                    )}
                    <button data-testid={`edit-${item.slug || item.id}`} onClick={() => openEdit(item)} className="p-1.5 rounded hover:bg-slate-200 text-slate-500" title="Edit">
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button data-testid={`delete-${item.slug || item.id}`} onClick={() => remove(item)} className="p-1.5 rounded hover:bg-rose-100 text-rose-500" title="Delete">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr><td colSpan={listColumns.length + 1} className="px-4 py-10 text-center text-slate-400">Nothing here yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 flex items-start justify-center overflow-y-auto p-4 md:p-10">
          <div className="bg-white rounded-lg w-full max-w-3xl p-6 md:p-8" data-testid="crud-form-modal">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading text-xl font-bold text-slate-900">{editing === "new" ? `New ${singular}` : `Edit ${singular}`}</h2>
              <button onClick={() => setEditing(null)} className="p-1.5 rounded hover:bg-slate-100 text-slate-500" data-testid="close-modal-button">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {fields.map((f) => <Field key={f.key} field={f} form={form} setForm={setForm} />)}
            </div>
            <div className="mt-8 flex justify-end gap-3">
              <button onClick={() => setEditing(null)} className="px-4 py-2.5 rounded-md border border-border text-sm font-semibold text-slate-600 hover:bg-slate-50 transition-colors duration-200">
                Cancel
              </button>
              <button data-testid="save-item-button" onClick={save} disabled={saving}
                className="px-6 py-2.5 rounded-md bg-sky-500 text-white text-sm font-semibold hover:bg-sky-600 active:scale-95 disabled:opacity-50 transition-colors duration-200">
                {saving ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ field, form, setForm }) {
  const val = form[field.key];
  const set = (v) => setForm((prev) => ({ ...prev, [field.key]: v }));
  const full = field.type === "markdown" || field.type === "textarea" || field.type === "rows" || field.full;

  const label = (
    <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wide mb-1.5">{field.label}</label>
  );
  const cls = "w-full px-3 py-2 rounded-md border border-border text-sm focus:outline-none focus:ring-2 focus:ring-sky-400";
  const testId = `field-${field.key}`;

  if (field.type === "textarea" || field.type === "markdown")
    return (
      <div className="md:col-span-2">
        {label}
        <textarea data-testid={testId} value={val || ""} onChange={(e) => set(e.target.value)} rows={field.type === "markdown" ? 12 : 3} className={`${cls} font-mono text-xs`} placeholder={field.type === "markdown" ? "Markdown supported: ## Heading, **bold**, - lists, | tables |" : ""} />
        {field.type === "markdown" && <p className="text-xs text-slate-400 mt-1">Markdown supported (headings, bold, lists, tables, links).</p>}
      </div>
    );

  if (field.type === "list")
    return (
      <div className="md:col-span-2">
        {label}
        <textarea data-testid={testId} value={(val || []).join("\n")} onChange={(e) => set(e.target.value.split("\n").filter(Boolean))} rows={3} className={cls} placeholder="One per line" />
      </div>
    );

  if (field.type === "tags")
    return (
      <div className={full ? "md:col-span-2" : ""}>
        {label}
        <input data-testid={testId} value={(val || []).join(", ")} onChange={(e) => set(e.target.value.split(",").map((s) => s.trim()).filter(Boolean))} className={cls} placeholder="Comma separated" />
      </div>
    );

  if (field.type === "number")
    return (
      <div>
        {label}
        <input data-testid={testId} type="number" step="0.1" value={val ?? ""} onChange={(e) => set(parseFloat(e.target.value) || 0)} className={cls} />
      </div>
    );

  if (field.type === "select")
    return (
      <div>
        {label}
        <select data-testid={testId} value={val || ""} onChange={(e) => set(e.target.value)} className={cls}>
          {(field.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      </div>
    );

  if (field.type === "image")
    return (
      <div className="md:col-span-2">
        {label}
        <div className="flex items-center gap-3">
          <input data-testid={testId} value={val || ""} onChange={(e) => set(e.target.value)} className={cls} placeholder="Image URL or upload →" />
          <label className="shrink-0 inline-flex items-center gap-1.5 px-3 py-2 rounded-md border border-border text-xs font-semibold text-slate-600 cursor-pointer hover:border-sky-400 transition-colors duration-200">
            <Upload className="w-3.5 h-3.5" aria-hidden="true" /> Upload
            <input type="file" accept="image/*" className="hidden" data-testid={`${testId}-upload`}
              onChange={async (e) => {
                const file = e.target.files[0];
                if (!file) return;
                const fd = new FormData();
                fd.append("file", file);
                try {
                  const { data } = await api.post("/admin/upload", fd);
                  set(data.url);
                  toast.success("Image uploaded");
                } catch { toast.error("Upload failed"); }
              }} />
          </label>
          {val && <img src={assetUrl(val)} alt="preview" className="w-10 h-10 rounded object-contain border border-border" />}
        </div>
      </div>
    );

  if (field.type === "rows")
    return (
      <div className="md:col-span-2">
        {label}
        <textarea
          data-testid={testId}
          value={(val || []).map((r) => `${r.group || ""} | ${r.criterion} | ${(r.values || []).join(" | ")}`).join("\n")}
          onChange={(e) =>
            set(
              e.target.value.split("\n").filter(Boolean).map((line) => {
                const parts = line.split("|").map((s) => s.trim());
                return { group: parts[0] || "General", criterion: parts[1] || "", values: parts.slice(2) };
              })
            )
          }
          rows={8}
          className={`${cls} font-mono text-xs`}
          placeholder="Group | Criterion | Value A | Value B  (one row per line)"
        />
        <p className="text-xs text-slate-400 mt-1">Format: Group | Criterion | Value for product 1 | Value for product 2</p>
      </div>
    );

  return (
    <div className={full ? "md:col-span-2" : ""}>
      {label}
      <input data-testid={testId} value={val || ""} onChange={(e) => set(e.target.value)} className={cls} />
    </div>
  );
}
